/**
 * "메뉴 보기" 버튼 — 디지털/PDF 메뉴 링크 노출(#793/#795).
 *
 * 매장이 메뉴를 하나도 등록하지 않았으면 렌더하지 않는다. `menuUrl`/`menuPdfUrl`
 * (store_meta.menu_urls) 이 둘 다 있으면 선택 다이얼로그를 띄우고, 하나만 있으면
 * 바로 새 탭으로 연다. 서버가 단일 계약(`menu_view_url`/`menu_view_kind`)만 내려주는
 * 구서버 호환을 위해 그 값을 최종 폴백으로 쓴다.
 *
 * @see #781 / admin#2111 설계 §7 / task-9 브리프
 */
import { useState } from 'react';
import { Utensils } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { WaitlistStrings } from './strings';
import { safeExternalUrl } from './utils';

interface WaitlistMenuButtonProps {
  t: WaitlistStrings;
  /** 서버 단일 계약 — `menuUrl`/`menuPdfUrl` 이 모두 없을 때만 쓰는 폴백. */
  menuViewUrl: string | null;
  /** 현재는 라벨/아이콘 분기에 쓰지 않는다 — 향후 단일 폴백 버튼 문구 분기용으로 계약만 받아둔다. */
  menuViewKind: 'pdf' | 'digital' | null;
  /** 디지털/PDF 둘 다 있을 때만 선택 다이얼로그로 분기(`store_meta.menu_urls`). */
  menuUrl?: string | null;
  menuPdfUrl?: string | null;
  className?: string;
}

export function WaitlistMenuButton({
  t,
  menuViewUrl,
  menuUrl,
  menuPdfUrl,
  className,
}: WaitlistMenuButtonProps) {
  const [open, setOpen] = useState(false);
  // 서버가 내려주는 URL 은 신뢰할 수 없는 입력이다 — javascript:/data: 스킴이나
  // 상대 경로가 섞이면 렌더 자체를 막는다(#794 Task 9 코드리뷰).
  const safeMenuUrl = safeExternalUrl(menuUrl);
  const safeMenuPdfUrl = safeExternalUrl(menuPdfUrl);
  const safeMenuViewUrl = safeExternalUrl(menuViewUrl);
  const both = Boolean(safeMenuUrl) && Boolean(safeMenuPdfUrl);
  const singleUrl = safeMenuUrl ?? safeMenuPdfUrl ?? safeMenuViewUrl;

  if (!both && !singleUrl) {
    return null;
  }

  const go = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        className={`w-full ${className ?? ''}`.trim()}
        onClick={() => (both ? setOpen(true) : go(singleUrl as string))}
      >
        <span className="inline-flex items-center gap-1.5">
          <Utensils className="size-4" />
          {t.verMenu}
        </span>
      </Button>
      {both && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.verMenu}</AlertDialogTitle>
              {/* Radix 접근성 경고 해소용 — 시각적으로는 숨긴다. */}
              <AlertDialogDescription className="sr-only">{t.verMenu}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-2.5">
              <Button size="lg" className="w-full" onClick={() => go(safeMenuUrl as string)}>
                {t.menuDigital}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full"
                onClick={() => go(safeMenuPdfUrl as string)}
              >
                {t.menuPdf}
              </Button>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>{t.cancelar}</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
